// Visitor tracking utility - Now uses server-side API
const API_BASE = process.env.REACT_APP_API_BASE || '/api';

// Check if route is an analytics page (should not be tracked)
const isAnalyticsPage = (route) => {
  const analyticsRoutes = [
    '/analytics',
    '/analytics/',
    '/analytics/analytics',
    '/analytics/reports',
    '/analytics/overview'
  ];
  return analyticsRoutes.includes(route) || route.startsWith('/analytics/');
};

// Check if route should be excluded (analytics pages or admin routes)
const shouldExcludeRoute = (route) => {
  // Exclude analytics pages
  if (isAnalyticsPage(route)) {
    return true;
  }
  // Exclude /admin routes
  if (route.startsWith('/admin')) {
    return true;
  }
  return false;
};

// Track visitor visit - sends to server to capture real IP
export const trackVisitor = async (route) => {
  // Don't track analytics pages or admin routes
  if (shouldExcludeRoute(route)) {
    return null;
  }

  try {
    const response = await fetch(`${API_BASE}/visitors/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ route }),
    });

    if (!response.ok) {
      throw new Error('Failed to track visitor');
    }

    const result = await response.json();
    return result.tracked ? result.data : null;
  } catch (error) {
    // Silently fail - don't interrupt user experience
    console.error('Error tracking visitor:', error);
    return null;
  }
};

// Cache for analytics data to reduce API calls
let analyticsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 2000; // 2 seconds cache

// Get all visitor data from server
export const getVisitorData = async () => {
  try {
    // Use cache if available and fresh
    const now = Date.now();
    if (analyticsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      return analyticsCache.allVisits || [];
    }

    const response = await fetch(`${API_BASE}/visitors/analytics`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch visitor data');
    }

    const result = await response.json();
    
    if (result.ok && result.data) {
      analyticsCache = result.data;
      cacheTimestamp = now;
      return result.data.allVisits || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error getting visitor data:', error);
    return [];
  }
};

// Get analytics data from server (all stats)
export const getAnalyticsData = async () => {
  try {
    // Use cache if available and fresh
    const now = Date.now();
    if (analyticsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      return analyticsCache;
    }

    const response = await fetch(`${API_BASE}/visitors/analytics`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch analytics data');
    }

    const result = await response.json();
    
    if (result.ok && result.data) {
      analyticsCache = result.data;
      cacheTimestamp = now;
      return result.data;
    }
    
    return {
      allVisits: [],
      totalVisitors: 0,
      uniqueVisitors: 0,
      routesVisited: [],
      visitorsByIP: []
    };
  } catch (error) {
    console.error('Error getting analytics data:', error);
    return {
      allVisits: [],
      totalVisitors: 0,
      uniqueVisitors: 0,
      routesVisited: [],
      visitorsByIP: []
    };
  }
};

// Get total visitors count (synchronous wrapper for backward compatibility)
export const getTotalVisitors = () => {
  // This will be updated by VisitorAnalytics to use async version
  return 0;
};

// Get unique visitors (synchronous wrapper for backward compatibility)
export const getUniqueVisitors = () => {
  // This will be updated by VisitorAnalytics to use async version
  return 0;
};

// Get routes visited (synchronous wrapper for backward compatibility)
export const getRoutesVisited = () => {
  // This will be updated by VisitorAnalytics to use async version
  return [];
};

// Get visitors by IP (synchronous wrapper for backward compatibility)
export const getVisitorsByIP = () => {
  // This will be updated by VisitorAnalytics to use async version
  return [];
};

// Clear analytics cache (force refresh)
export const clearAnalyticsCache = () => {
  analyticsCache = null;
  cacheTimestamp = null;
};

// Clear all visitor data (server-side)
export const clearVisitorData = async () => {
  try {
    // Note: This would require a DELETE endpoint on the server
    // For now, we'll just clear the cache
    clearAnalyticsCache();
    console.warn('clearVisitorData: Server-side clear not implemented. Cache cleared only.');
  } catch (error) {
    console.error('Error clearing visitor data:', error);
  }
};

